import { useState, useEffect } from "react";
import { Link } from "react-router";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useCart } from "./CartContext";
import { useWishlist } from "./WishlistContext";
import { useLanguage } from "./LanguageContext";
import { ShoppingBag, Heart } from "lucide-react";
import { toast } from "sonner";
import { useAdminData, type AdminProduct } from "./admin/AdminDataContext";
import { localized } from "./admin/TranslationsPanel";
import { useTheme } from "./ThemeInjector";
import { isSupabaseConnected, fetchProducts } from "./supabase";
import type { I18nMap } from "./admin/TranslationsPanel";

const tagStyles: Record<string, string> = {
  new: "bg-[#6F7F63] text-white",
  limited: "bg-[#C7A76C] text-white",
  "sold out": "bg-[#6F675F] text-white",
};

function mapRowToProduct(row: {
  id: number;
  slug: string;
  name: string;
  price: number;
  category: string;
  tag?: string | null;
  description?: string | null;
  details?: unknown;
  images?: string[] | null;
  related_ids?: number[] | null;
  stock_quantity?: number;
  low_stock_threshold?: number;
  i18n?: Record<string, unknown> | null;
}): AdminProduct {
  return {
    id: row.id,
    slug: row.slug,
    name: row.name,
    price: Number(row.price),
    category: row.category,
    tag: (row.tag as AdminProduct["tag"]) ?? "",
    description: row.description ?? "",
    details: Array.isArray(row.details) ? (row.details as { label: string; value: string }[]) : [],
    images: Array.isArray(row.images) ? row.images : [],
    relatedIds: Array.isArray(row.related_ids) ? row.related_ids : [],
    stock_quantity: row.stock_quantity ?? 0,
    low_stock_threshold: row.low_stock_threshold ?? 5,
    i18n: (row.i18n as I18nMap) ?? {},
  };
}

export function ProductSection() {
  const { products: contextProducts, categories: contextCategories, siteConfig } = useAdminData();
  const wishlistEnabled = siteConfig.features?.wishlist !== false;
  const [products, setProducts] = useState<AdminProduct[]>(() =>
    isSupabaseConnected() ? [] : contextProducts
  );
  /** Language-independent key for "all" so filter works in every locale */
  const ALL_FILTER_KEY = "__all__";
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER_KEY);
  const { addItem } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { t, lang } = useLanguage();
  const theme = useTheme();

  useEffect(() => {
    if (!isSupabaseConnected()) {
      setProducts(contextProducts);
      return;
    }
    fetchProducts()
      .then(({ data, error }) => {
        if (error) {
          console.warn("[MOECHA] Failed to fetch products:", error.message);
          setProducts(contextProducts);
          return;
        }
        setProducts((data ?? []).map((r) => mapRowToProduct(r as Parameters<typeof mapRowToProduct>[0])));
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fetch once on mount when Supabase connected

  useEffect(() => {
    if (isSupabaseConnected()) return;
    setProducts(contextProducts);
  }, [contextProducts]);

  const categories = isSupabaseConnected()
    ? [...new Set(products.map((p) => p.category))]
    : contextCategories;
  const filterOptions = [
    { id: ALL_FILTER_KEY, label: t("products.all") },
    ...categories.map((c) => ({ id: c, label: c })),
  ];

  const filtered =
    activeFilter === ALL_FILTER_KEY
      ? products
      : products.filter((p) => p.category === activeFilter);

  const handleAddToCart = (product: AdminProduct, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (product.stock_quantity === 0) return;
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.images[0],
    });
    toast.success(`${localized(product, "name", lang)} ${t("products.added")}`);
  };

  const handleWishlistToggle = (product: AdminProduct, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
    toast.success(
      isWishlisted(product.id) ? t("wishlist.removed") : t("wishlist.added")
    );
  };

  return (
    <section id="collection" className="py-24 px-6 max-w-7xl mx-auto">
      {/* Title */}
      <div className="text-center mb-16">
        <p
          className="tracking-[0.3em] uppercase mb-3"
          style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.accent }}
        >
          {t("products.label")}
        </p>
        <h2
          className="tracking-[0.1em]"
          style={{ fontFamily: theme.fontHeading, fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 400, color: theme.fg }}
        >
          {t("products.title")}
        </h2>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap justify-center gap-3 mb-16" role="group" aria-label="Product category filters">
        {filterOptions.map((opt) => (
          <button
            key={opt.id}
            onClick={() => setActiveFilter(opt.id)}
            className="px-5 py-2 rounded-full tracking-[0.15em] uppercase transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] focus:ring-offset-2"
            style={{
              fontFamily: theme.fontBody,
              fontSize: "11px",
              fontWeight: 400,
              backgroundColor: activeFilter === opt.id ? theme.primary : "transparent",
              color: activeFilter === opt.id ? theme.btnText : theme.muted,
            }}
            aria-pressed={activeFilter === opt.id}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {filtered.map((product) => (
          <Link
            key={product.id}
            to={`/product/${product.slug}`}
            className="group"
            aria-label={`${product.name} - €${product.price}`}
          >
            <div className="relative overflow-hidden rounded-xl mb-5 bg-[#F2EEE8]">
              <ImageWithFallback
                src={product.images[0]}
                alt={product.name}
                className={`w-full aspect-[3/4] object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out ${
                  product.stock_quantity === 0 ? "opacity-50" : ""
                }`}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-500" />

              {/* Tag */}
              {product.tag && (
                <span
                  className={`absolute top-3 left-3 px-3 py-1 rounded-full tracking-[0.15em] uppercase ${tagStyles[product.tag]}`}
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "10px", fontWeight: 400 }}
                >
                  {product.tag}
                </span>
              )}

              {/* Low stock badge */}
              {product.stock_quantity > 0 && product.stock_quantity <= product.low_stock_threshold && !product.tag && (
                <span
                  className="absolute top-3 left-3 px-3 py-1 rounded-full tracking-[0.15em] uppercase bg-[#C7A76C]/90 text-white"
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "10px", fontWeight: 400 }}
                >
                  {t("products.low_stock")}
                </span>
              )}

              {/* Out of stock overlay */}
              {product.stock_quantity === 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/40">
                  <span
                    className="px-4 py-2 bg-[#2D2A26]/80 text-white rounded-full tracking-[0.2em] uppercase"
                    style={{ fontFamily: "'Jost', sans-serif", fontSize: "11px" }}
                  >
                    {t("products.sold_out")}
                  </span>
                </div>
              )}

              {/* Wishlist button (only when feature enabled) */}
              {wishlistEnabled && (
                <button
                  onClick={(e) => handleWishlistToggle(product, e)}
                  className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] ${
                    isWishlisted(product.id)
                      ? "bg-[#8A6D63] text-white shadow-md"
                      : "bg-white/80 backdrop-blur-sm text-[#6F675F] opacity-0 group-hover:opacity-100"
                  }`}
                  aria-label={isWishlisted(product.id) ? "Remove from wishlist" : "Add to wishlist"}
                >
                  <Heart
                    size={14}
                    className={isWishlisted(product.id) ? "fill-white" : ""}
                  />
                </button>
              )}

              {/* Add to cart button */}
              {product.stock_quantity > 0 && (
                <button
                  onClick={(e) => handleAddToCart(product, e)}
                  className="absolute bottom-3 right-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-[#2D2A26] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#6F7F63] hover:text-white shadow-[0_8px_30px_rgba(40,32,20,0.06)] focus:outline-none focus:ring-2 focus:ring-[#C7A76C]"
                  aria-label={`${t("products.add_to_cart")} ${product.name}`}
                >
                  <ShoppingBag size={16} />
                </button>
              )}
            </div>
            <h3
              className="tracking-[0.08em] mb-1"
              style={{ fontFamily: theme.fontHeading, fontSize: "18px", fontWeight: 500, color: theme.fg }}
            >
              {localized(product, "name", lang)}
            </h3>
            <p
              className="tracking-[0.1em]"
              style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
            >
              &euro;{product.price}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}